import { differenceInDays, format, isAfter, isEqual, isValid, parseISO } from 'date-fns';
import pick from 'lodash/pick';
import { v4 as uuidv4 } from 'uuid';

import { CUSTOM_SEND_PRE_CREATED_ENGAGEMENT_ID } from '../constants/bucket';
import { DateFormatsEnum } from '../constants/date';
import { NO_FULFILLMENT_CENTER_ITEM } from '../constants/fulfillmentCenters';
import {
  AWAITING_ADDRESS,
  AWAITING_SHIPMENT,
  CANCELED,
  COMPLETED,
  CREATED_AT,
  DATE_TO_SEND,
  DELIVERED,
  EXPIRED,
  GIFT_BUDGET_SPENT_OF_SEPARATOR,
  IN_FULFILLMENT,
  ITEM_SOURCE_ORDER,
  PROCESSING,
  REPORT_STATUS,
  SHIPPED,
  VIEWED,
} from '../constants/reports';
import {
  DELAYED_SHIPPING_URL,
  RECEIVER_COMPANY_NAME,
  RECEIVER_FIRST_NAME,
  RECEIVER_LAST_NAME,
  RECEIVER_PHONE,
  TRACKING_URL,
} from '../constants/shipping';
import { MESSAGE } from '../constants/templates';
import { ISendItemsInfo, TEngagementCandidate } from '../types/bucket';
import {
  IItemSourceEvidence,
  ItemSourceEnum,
  MARKETPLACE_CURRENCY_CODE,
  MARKETPLACE_FULFILLMENT_CENTER_NAME,
} from '../types/marketplace';
import { IOneLink, IOneLinkWithCampaignSummaries } from '../types/oneLink';
import {
  EngagementSourceTypesEnum,
  GiftBudgetUsageKindEnum,
  IReport,
  IReportGiftBudget,
  IReportGiftBudgetUsage,
  IReportWithCampaignSummaries,
  ReportStatusEnum,
} from '../types/reports';
import { CopyableLinkTypesEnum } from '../types/shell';
import { IBulkPayloadProps, IReceiverInfoDetailsFormValues } from '../types/shipping';
import { isDigitalBox } from './campaigns';
import { formatDate, getUTCDate, isValidDate } from './date';
import { isItemDigital } from './inventories';
import {
  hasMarketplacePickOptions,
  isMarketplaceCampaignId,
  isMarketplaceEngagementItem,
  isMarketplaceOptionId,
  isRealCampaignId,
  offersMarketplaceContent,
} from './marketplaceSend';

const getScheduledReportStatusAndDate = (report: IReport) => {
  const date = formatDate(report.date_to_send, DateFormatsEnum.ddMMMyyyy);
  const dateLabel = 'Scheduled Date';
  const status = REPORT_STATUS[report.status || PROCESSING];

  return { ...status, date, dateLabel };
};

const getRegularReportStatusAndDate = (report: IReport) => {
  let date;
  let dateLabel;
  let status;
  const isDigital = isDigitalReport(report);

  if (isDigital) {
    const item = report.items?.[0];
    date = item?.digital_claim_date ? formatDate(item.digital_claim_date, DateFormatsEnum.ddMMMyyyy) : '';
    dateLabel = 'Claimed Date';
  } else {
    // check for "Viewed" status (frontend-only)
    if (isAwaitingAddress(report) && isValidDate(new Date(report.delayed_shipping_page_view_date))) {
      date = formatDate(report.delayed_shipping_page_view_date, DateFormatsEnum.ddMMMyyyy);
      status = REPORT_STATUS[VIEWED];
      dateLabel = 'Viewed Date';
    } else {
      date = report.package_delivered_date ? formatDate(report.package_delivered_date, DateFormatsEnum.ddMMMyyyy) : '';
      dateLabel = 'Delivered Date';
    }
  }

  if (isCompleted(report)) {
    status = REPORT_STATUS[COMPLETED];
  }

  if (!status) {
    status = REPORT_STATUS[report.status || PROCESSING];
  }

  return { ...status, date, dateLabel };
};

export const isDigitalReport = (report: IReport | IOneLink | IOneLinkWithCampaignSummaries) => {
  return !!report.items?.length && report.items.every(isItemDigital);
};

export const isDigitalPYGReport = (report: IReportWithCampaignSummaries | IOneLinkWithCampaignSummaries) =>
  !!report.pick_campaigns?.length && report.pick_campaigns.every(isDigitalBox) && !hasMarketplacePickOptions(report);

export const isCustomReport = (report: IReport | IOneLink) => {
  return report?.pre_created_engagement_id === CUSTOM_SEND_PRE_CREATED_ENGAGEMENT_ID;
};

export const isReportViewed = (report: Partial<Pick<IReport, 'status'>>) => {
  const { status } = report || {};
  return status === VIEWED;
};

export const isAwaitingAddress = (report: Partial<Pick<IReport, 'status'>>) => {
  const { status } = report || {};
  return status === AWAITING_ADDRESS;
};

export const isAwaitingShipment = (report: Partial<Pick<IReport, 'status'>>) => {
  const { status } = report || {};
  return status === AWAITING_SHIPMENT;
};

export const isShipped = ({ status }: Pick<IReport, 'status'>) => status === SHIPPED;

export const isDelivered = ({ status }: Pick<IReport, 'status'>) => status === DELIVERED;

export const isCompleted = ({ status, easypost_id }: Pick<IReport, 'status' | 'easypost_id'>) =>
  status === SHIPPED && !easypost_id;

export const isCanceled = ({ status }: Pick<IReport, 'status'>) => status === CANCELED;

export const isExpired = ({ status }: Pick<IReport, 'status'>) => status === EXPIRED;

export const isNonEditableStatus = ({ status }: Pick<IReport, 'status'>) =>
  status === IN_FULFILLMENT || status === SHIPPED || status === DELIVERED || status === CANCELED || status === EXPIRED;

export const isNew = ({ status }: Pick<IReport, 'status'>) => status === ReportStatusEnum.New;

export const getShipmentInfoIsNotConfirmed = ({ status }: Pick<IReport, 'status'>) => {
  return isNew({ status }) || isReportViewed({ status }) || isAwaitingAddress({ status });
};

export const getReportStatusAndDate = (report: IReport) => {
  const createdAtDate = formatDate(report.created_at, DateFormatsEnum.ddMMMyyyy);
  const nextReminderDate = formatDate(report.receiver_next_reminder_send_date, DateFormatsEnum.ddMMMyyyy);
  const onHoldUntilDate = formatDate(report.on_hold_until, DateFormatsEnum.ddMMMyyyy);
  const delayedShippingExpirationDate = format(
    getUTCDate(report.delayed_shipping_expiration),
    DateFormatsEnum.FullHumanReadable,
  );

  if (isValidScheduledEngagement(report)) {
    return {
      ...getScheduledReportStatusAndDate(report),
      createdAtDate,
      nextReminderDate,
      onHoldUntilDate,
      delayedShippingExpirationDate,
    };
  }

  return {
    ...getRegularReportStatusAndDate(report),
    createdAtDate,
    onHoldUntilDate,
    nextReminderDate,
    delayedShippingExpirationDate,
  };
};

export const sortReportsByCreatedTime = (isScheduled: boolean) => {
  const prop = isScheduled ? DATE_TO_SEND : CREATED_AT;

  return (current: IReport, next: IReport) => {
    // If the dates are equal we need to check the microseconds
    // When doing a bulk - sends will have the difference in microseconds and not milliseconds
    // Javascript just omits everything after the milliseconds
    if (isEqual(new Date(next[prop]), new Date(current[prop]))) {
      // This RegExp looks for the numbers between the period symbol (dot) and `Z`
      // const microsecondsRegex = /(?<=\.)[0-9]+(?!=Z)/m;
      const microsecondsRegex = /\.[.0-9]+(?!=Z)/m;

      const nextMs = parseFloat(microsecondsRegex.exec(next[prop])?.[0] || '');
      const currentMs = parseFloat(microsecondsRegex.exec(current[prop])?.[0] || '');

      // If the dates are not equal we should sort them descending
      if (nextMs !== currentMs) {
        return nextMs - currentMs;
      }

      return 0;
    }

    // If the dates are not equal we should sort them descending
    return isAfter(new Date(next[prop]), new Date(current[prop])) ? 1 : -1;
  };
};

export const populateReportItemsWithId = (report: IReport) => ({
  ...report,
  items: report.items?.map((item) => ({ ...item, id: uuidv4() })),
});

export const isOneLinkEngagement = ({ engagement_source_type }: Pick<IReport, 'engagement_source_type'>) =>
  engagement_source_type ? engagement_source_type === EngagementSourceTypesEnum.OneLink : false;

export const isDirectSendEngagement = ({ engagement_source_type }: Pick<IReport, 'engagement_source_type'>) =>
  engagement_source_type ? engagement_source_type === EngagementSourceTypesEnum.DirectSend : false;

export const isProtectedEngagement = ({ password, allowed_domains }: Pick<IOneLink, 'password' | 'allowed_domains'>) =>
  Boolean(password || allowed_domains);

export const is2FAProtectedEngagement = ({ enabled_2fa }: Pick<IOneLink, 'enabled_2fa'>) => Boolean(enabled_2fa);

export const isValidScheduledEngagement = (engagement: IReport) => {
  const dateToSend = engagement && engagement.date_to_send ? new Date(engagement.date_to_send) : false;
  return dateToSend && isValidDate(dateToSend);
};

export const isPYGReport = ({ pick_campaign_ids }: { pick_campaign_ids: string[] | null }) =>
  Boolean(pick_campaign_ids?.length);

export const getReportCopyableInfo = (report: IReport) => {
  if (isValidScheduledEngagement(report)) {
    return [];
  }

  const result = [];

  const digitals = report.items?.filter(isItemDigital);
  if (digitals && digitals.length) {
    result.push({
      type: CopyableLinkTypesEnum.DigitalGift,
      data: digitals.map(({ name, digital_gift_url }) => ({
        label: name,
        url: digital_gift_url,
      })),
    });
  }

  if (report[DELAYED_SHIPPING_URL]) {
    result.push({
      type: CopyableLinkTypesEnum.Shipping,
      data: {
        label: 'Claim link',
        url: report[DELAYED_SHIPPING_URL],
      },
    });
  }

  if (report[TRACKING_URL]) {
    result.push({
      type: CopyableLinkTypesEnum.Tracking,
      data: {
        label: 'Tracking URL',
        url: report[TRACKING_URL],
      },
    });
  }

  return result;
};

export const getEditableSendFields = (
  report: IReport | null | undefined,
): IReceiverInfoDetailsFormValues | undefined => {
  if (!report) {
    return;
  }

  return pick(report, [RECEIVER_FIRST_NAME, RECEIVER_LAST_NAME, RECEIVER_COMPANY_NAME, RECEIVER_PHONE, MESSAGE]);
};

export const mapSendItemsForEditSendRequest = (updates?: ISendItemsInfo['items_info']) => {
  if (!updates) {
    return;
  }

  return {
    items_info: updates.map(({ sku_options, ...item }) => {
      if (sku_options && sku_options.length) {
        return { ...item, sku_option_ids: sku_options.map(({ item_id }) => item_id) };
      }
      return item;
    }),
  };
};

export const calculateDaysLeft = (endDate: string) => {
  const targetDate = parseISO(endDate);
  const isValidTargetDate = isValid(targetDate);

  if (!isValidTargetDate) {
    return;
  }

  const currentDate = getUTCDate(new Date().toISOString());

  const daysLeft = differenceInDays(targetDate, currentDate);

  switch (true) {
    case daysLeft > 1:
      return `${daysLeft} days left`;
    case daysLeft === 1:
      return '1 day left';
    case daysLeft === 0 && isAfter(targetDate, currentDate):
      return 'today';
    default:
      return 'expired';
  }
};

export const transformReportAddressToCommonAddress = (values: TEngagementCandidate | IReportWithCampaignSummaries) => {
  const {
    receiver_address1,
    receiver_address2,
    receiver_city,
    receiver_country,
    receiver_state,
    receiver_zip,
    receiver_company_name,
    receiver_tax_number,
  } = values;

  return {
    address1: receiver_address1,
    address2: receiver_address2,
    city: receiver_city,
    country: receiver_country,
    state: receiver_state,
    zip: receiver_zip,
    company_name: receiver_company_name,
    tax_number: receiver_tax_number,
  };
};

export const prepareShippingRequestData = (data: IBulkPayloadProps) => {
  const { receiver_address, ...rest } = data;

  if (receiver_address) {
    const { address1, address2, city, country, state, zip, company_name, tax_number } = receiver_address;

    return {
      ...rest,
      receiver_address1: address1,
      receiver_address2: address2 || '',
      receiver_city: city,
      receiver_country: country,
      receiver_state: state || '',
      receiver_zip: zip,
      receiver_tax_number: tax_number || '',
      receiver_company_name: company_name || '',
    };
  }
  return data;
};

const formatReportDaysInStatusText = (days: number) => {
  switch (true) {
    case days > 1:
      return `${days} days in this status`;
    case days === 1:
      return '1 day in this status';
    default:
      return 'Less than a day';
  }
};

const getDigitalReportStatusDate = (report: IReport): Date | null => {
  const { items, created_at, canceled_at, status } = report;
  const firstItem = items?.[0];

  const { digital_gift_expiration, digital_claim_date } = firstItem ?? {};

  switch (status) {
    case ReportStatusEnum.GiftClaimed:
      return digital_claim_date ? new Date(digital_claim_date) : null;
    case ReportStatusEnum.GiftExpired:
      return digital_gift_expiration ? new Date(digital_gift_expiration) : null;
    case ReportStatusEnum.Canceled:
      return canceled_at ? new Date(canceled_at) : null;
    default:
      // For NEW status and others
      return created_at ? new Date(created_at) : null;
  }
};

const getPhysicalReportStatusDate = (report: IReport): Date | null => {
  const {
    created_at,
    delayed_shipping_page_view_date,
    delayed_shipping_url,
    delayed_shipping_submission_date,
    package_shipped_date,
    package_delivered_date,
    canceled_at,
    status,
    delayed_shipping_expiration,
  } = report;

  switch (status) {
    case AWAITING_ADDRESS:
      return created_at ? new Date(created_at) : null;
    case VIEWED:
      return delayed_shipping_page_view_date ? new Date(delayed_shipping_page_view_date) : null;
    case AWAITING_SHIPMENT:
    case IN_FULFILLMENT:
      return !!delayed_shipping_url // is DS?
        ? new Date(delayed_shipping_submission_date)
        : new Date(created_at);
    case SHIPPED:
      return package_shipped_date ? new Date(package_shipped_date) : null;
    case DELIVERED:
      return package_delivered_date ? new Date(package_delivered_date) : null;
    case CANCELED:
      return isValidDate(new Date(canceled_at)) ? new Date(canceled_at) : new Date(delayed_shipping_expiration);
    default:
      return created_at ? new Date(created_at) : null;
  }
};

const getReportStatusBaseDate = (report: IReport): Date | null => {
  // needs to be checked explicitly until we merge the statuses into one as it is not
  if (report.status === ReportStatusEnum.Canceled && report.canceled_at) {
    return new Date(report.canceled_at);
  }

  if (isValidScheduledEngagement(report)) {
    return null;
  }

  // Determine if digital or physical and get the appropriate date
  return isDigitalReport(report) ? getDigitalReportStatusDate(report) : getPhysicalReportStatusDate(report);
};

export const getReportStatusTooltipInfo = (report: IReport): string => {
  const baseDate = getReportStatusBaseDate(report);
  const invalidDate = !baseDate || !isValid(baseDate) || !isValidDate(baseDate);

  if (invalidDate) {
    return '';
  }

  // Calculate calendar days by comparing dates at midnight
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const statusDate = new Date(baseDate);
  statusDate.setHours(0, 0, 0, 0);

  const days = differenceInDays(today, statusDate);
  return formatReportDaysInStatusText(days);
};

export const getClaimUrl = (report: IReport | null) => {
  if (!report) {
    return '';
  }
  // if it is digital, it can only have 1 digital item and no other
  const firstItem = report.items?.[0];

  const isDigitalSend = isItemDigital(firstItem);

  if (isDigitalSend) {
    return firstItem?.digital_gift_url ?? '';
  }

  // For non-digital sends, return the delayed_shipping_url
  return report.delayed_shipping_url ?? '';
};

// Picked PYG has campaign name, items array and pick_campaign_ids at the same time,
// while non-PYG sends do not have pick_campaign_ids and have items array with campaign_name
export const isCampaignPicked = (report: Pick<IReport, 'pick_campaign_ids' | 'campaign_name'>) => {
  return !report.pick_campaign_ids || !!report.campaign_name;
};

type TClaimSubmissionEvidence = Partial<Pick<IReport, 'delayed_shipping_submission_date' | 'engagement_source_type'>>;

type TItemSourceEvidence = IItemSourceEvidence & TClaimSubmissionEvidence;

export const isClaimSubmitted = (report?: TClaimSubmissionEvidence | null): boolean => {
  if (!report) {
    return false;
  }

  if (report.engagement_source_type === EngagementSourceTypesEnum.OneLink) {
    return true;
  }

  const submittedAt = report.delayed_shipping_submission_date;

  return !!submittedAt && isValidDate(new Date(submittedAt));
};

const toItemSources = (hasWarehouse: boolean, hasMarketplace: boolean): ItemSourceEnum[] => {
  if (!hasWarehouse && !hasMarketplace) {
    return [ItemSourceEnum.IeWarehouse];
  }

  return ITEM_SOURCE_ORDER.filter((source) => (source === ItemSourceEnum.IeWarehouse ? hasWarehouse : hasMarketplace));
};

const getOfferedItemSources = (report: TItemSourceEvidence): ItemSourceEnum[] => {
  const campaignId = report.pre_created_engagement_id ?? '';
  const items = report.items ?? [];
  const pickIds = (report.pick_campaign_ids ?? []).filter((id) => typeof id === 'string' && id.trim() !== '');

  const hasMarketplace =
    isMarketplaceCampaignId(campaignId) || offersMarketplaceContent(report) || items.some(isMarketplaceEngagementItem);
  const hasWarehouse =
    isRealCampaignId(campaignId) ||
    items.some((item) => !isMarketplaceEngagementItem(item)) ||
    pickIds.some((id) => !isMarketplaceOptionId(id));

  return toItemSources(hasWarehouse, hasMarketplace);
};

const getPickedItemSources = (report: TItemSourceEvidence): ItemSourceEnum[] => {
  const campaignId = report.pre_created_engagement_id ?? '';
  const items = report.items ?? [];

  const hasMarketplace = isMarketplaceCampaignId(campaignId) || items.some(isMarketplaceEngagementItem);
  const hasWarehouse = isRealCampaignId(campaignId) || items.some((item) => !isMarketplaceEngagementItem(item));

  return toItemSources(hasWarehouse, hasMarketplace);
};

export const getReportItemSources = (report?: TItemSourceEvidence | null): ItemSourceEnum[] => {
  if (!report) {
    return [ItemSourceEnum.IeWarehouse];
  }

  return isClaimSubmitted(report) ? getPickedItemSources(report) : getOfferedItemSources(report);
};

export const hasMarketplaceItemSource = (report?: TItemSourceEvidence | null): boolean =>
  getReportItemSources(report).includes(ItemSourceEnum.Marketplace);

export const hasMixedItemSources = (report?: TItemSourceEvidence | null): boolean =>
  getReportItemSources(report).length > 1;

export const getReportGiftBudget = (
  report?: Partial<Pick<IItemSourceEvidence, 'gift_budget'>> | null,
): IReportGiftBudget | null => {
  const budget = report?.gift_budget;

  if (!budget || typeof budget.value !== 'number') {
    return null;
  }

  return {
    value: budget.value,
    currency: budget.currency ?? MARKETPLACE_CURRENCY_CODE,
    used: budget.used_budget ?? 0,
  };
};

export const getReportWarehouseNames = (report?: Partial<Pick<IReport, 'fulfillment_center_names'>> | null): string =>
  (report?.fulfillment_center_names ?? [])
    .filter(
      (name) => !!name && name !== NO_FULFILLMENT_CENTER_ITEM.name && name !== MARKETPLACE_FULFILLMENT_CENTER_NAME,
    )
    .join(', ');

export const formatMarketplaceSpent = (amount: number, currency = MARKETPLACE_CURRENCY_CODE): string =>
  amount.toLocaleString('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export const getReportGiftBudgetUsage = (
  report?: (TClaimSubmissionEvidence & Partial<Pick<IReport, 'gift_budget'>>) | null,
): IReportGiftBudgetUsage | null => {
  const budget = getReportGiftBudget(report);

  if (!budget) {
    return null;
  }

  const spent = budget.used ?? 0;
  const remaining = budget.value - spent;

  if (!isClaimSubmitted(report)) {
    return { kind: GiftBudgetUsageKindEnum.Pending, budget, spent, remaining };
  }

  return {
    kind: spent > 0 ? GiftBudgetUsageKindEnum.Spent : GiftBudgetUsageKindEnum.NotUsed,
    budget,
    spent,
    remaining,
  };
};

export const formatGiftBudgetSpentOf = ({ spent, budget }: IReportGiftBudgetUsage): string =>
  `${formatMarketplaceSpent(spent, budget.currency)} ${GIFT_BUDGET_SPENT_OF_SEPARATOR} ${formatMarketplaceSpent(
    budget.value,
    budget.currency,
  )}`;
